/**
 * Message Cache Service
 * Caches decrypted messages in IndexedDB to avoid re-decrypting Signal Protocol messages
 * Signal Protocol can only decrypt each ciphertext once, so we cache the plaintext client-side
 */

const DB_NAME = 'messageCache';
const DB_VERSION = 1;
const STORE_NAME = 'decryptedMessages';

let dbPromise: Promise<IDBDatabase> | null = null;

function getDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'messageId' });
      }
    };
  });

  return dbPromise;
}

/**
 * Cache a decrypted message content
 */
export async function cacheDecryptedMessage(messageId: string, decryptedContent: string): Promise<void> {
  try {
    const db = await getDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    await store.put({ messageId, decryptedContent, cachedAt: Date.now() });
  } catch (error) {
    // Silently fail - cache is optional
    console.warn('Failed to cache decrypted message', error);
  }
}

/**
 * Get cached decrypted content for a message
 */
export async function getCachedDecryptedMessage(messageId: string): Promise<string | null> {
  try {
    const db = await getDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(messageId);
    
    return new Promise((resolve) => {
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.decryptedContent : null);
      };
      request.onerror = () => resolve(null);
    });
  } catch (error) {
    return null;
  }
}

/**
 * Check if a message is cached
 */
export async function isMessageCached(messageId: string): Promise<boolean> {
  const cached = await getCachedDecryptedMessage(messageId);
  return cached !== null;
}

/**
 * Clear all cached messages (for logout or reset)
 */
export async function clearMessageCache(): Promise<void> {
  try {
    const db = await getDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    await store.clear();
  } catch (error) {
    console.warn('Failed to clear message cache', error);
  }
}

